import { useEffect, useMemo, useState } from 'react'
import { teamInfo, teamLogo } from '../teams'

const API_URL = import.meta.env.VITE_API_URL || ''

export const TIERS = ['S', 'A', 'B', 'C', 'F']
export const TIER_POINTS = { S: 10, A: 6, B: 3, C: 0, F: -3 }

export const RANGES = [
  { id: '30', label: '30D', days: 30 },
  { id: '90', label: '90D', days: 90 },
  { id: '365', label: '1Y', days: 365 },
  { id: 'all', label: 'All', days: Infinity },
]

export const parseDay = d => new Date(`${d}T00:00:00Z`)

const shiftDay = (d, days) => {
  const date = parseDay(d)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

export const imageUrl = path => (!path ? null : path.startsWith('http') ? path : `${API_URL}${path}`)

async function getJson(url) {
  const res = await fetch(`${API_URL}${url}`)
  const data = await res.json()
  if (!res.ok || data.status !== 'ok') throw new Error(data.message || 'Request failed')
  return data
}

function useFetch(url) {
  const [state, setState] = useState({ data: null, error: null, loading: !!url })
  useEffect(() => {
    if (!url) {
      setState({ data: null, error: null, loading: false })
      return
    }
    let cancelled = false
    setState(s => ({ ...s, loading: true }))
    getJson(url)
      .then(data => !cancelled && setState({ data, error: null, loading: false }))
      .catch(error => !cancelled && setState({ data: null, error, loading: false }))
    return () => { cancelled = true }
  }, [url])
  return state
}

// Turns the raw API payload into per-team series aligned on every snapshot
// date (a team's value carries forward until its next snapshot).
function buildLeague(raw) {
  const dates = raw.dates
  const teams = raw.teams.map(t => {
    const info = teamInfo(t.name)
    const byDate = new Map(t.points.map(p => [p.date, p]))
    let last = null
    const series = dates.map(date => {
      const p = byDate.get(date)
      if (p) last = p
      return last ? { date, rank: last.rank, score: last.score, complete: last.complete, point: p || null } : null
    })
    return { id: t.id, name: t.name, acronym: info.acronym, accent: info.accent, logo: teamLogo(t.name), points: t.points, series }
  })
  const byId = new Map(teams.map(t => [t.id, t]))
  const byAcronym = new Map(teams.map(t => [t.acronym, t]))
  const dateIndex = new Map(dates.map((d, i) => [d, i]))
  const trackedSince = raw.teams.flatMap(t => t.points.filter(p => p.tracked).map(p => p.date)).sort()[0] || null
  return { dates, teams, byId, byAcronym, dateIndex, trackedSince }
}

export function useLeagueHistory() {
  const { data, error, loading } = useFetch('/api/history')
  const league = useMemo(() => (data ? buildLeague(data) : null), [data])
  return { league, error, loading }
}

export function useTeamRosters(teamId) {
  const { data, loading } = useFetch(teamId ? `/api/history/teams/${teamId}/rosters` : null)
  return { snapshots: data?.snapshots || null, loading }
}

// Snapshot dates inside the chosen range, ending at the latest snapshot.
export function rangeDates(league, rangeId) {
  const { days } = RANGES.find(r => r.id === rangeId) || RANGES.at(-1)
  if (!league.dates.length || days === Infinity) return league.dates
  const start = shiftDay(league.dates.at(-1), -days)
  const inRange = league.dates.filter(d => d >= start)
  // Keep the snapshot just before the window so the first change is visible.
  const firstIdx = league.dateIndex.get(inRange[0])
  return firstIdx > 0 ? [league.dates[firstIdx - 1], ...inRange] : inRange
}

export const valueAt = (team, date, league) => team.series[league.dateIndex.get(date)] || null

export const previousDate = (league, date) => league.dates[league.dateIndex.get(date) - 1] || null

// League table on a date, with movement since the previous snapshot.
export function standingsAt(league, date) {
  const prev = previousDate(league, date)
  return league.teams
    .map(team => {
      const v = valueAt(team, date, league)
      const p = prev ? valueAt(team, prev, league) : null
      if (!v) return null
      return {
        team,
        rank: v.rank,
        score: v.score,
        complete: v.complete,
        rankDelta: p ? p.rank - v.rank : 0,
        scoreDelta: p ? v.score - p.score : 0,
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank)
}

// Everything that happened to one team on one snapshot date.
export function teamChange(league, team, date) {
  const prev = previousDate(league, date)
  const v = valueAt(team, date, league)
  const p = prev ? valueAt(team, prev, league) : null
  if (!v || !p) return null
  const passedBy = []
  const passed = []
  for (const other of league.teams) {
    if (other.id === team.id) continue
    const ov = valueAt(other, date, league)
    const op = valueAt(other, prev, league)
    if (!ov || !op) continue
    if (op.rank > p.rank && ov.rank < v.rank) passedBy.push(other)
    if (op.rank < p.rank && ov.rank > v.rank) passed.push(other)
  }
  return {
    date,
    prevDate: prev,
    rank: v.rank,
    prevRank: p.rank,
    rankDelta: p.rank - v.rank,
    score: v.score,
    scoreDelta: v.score - p.score,
    events: v.point?.events || [],
    tracked: !!v.point?.tracked,
    passedBy,
    passed,
  }
}

const isMeaningful = c => c && (c.rankDelta !== 0 || Math.abs(c.scoreDelta) > 1e-6 || c.events.length > 0)

// Change log for a team within a set of dates, newest first.
export function teamChanges(league, team, dates) {
  return dates
    .slice(1)
    .map(d => teamChange(league, team, d))
    .filter(isMeaningful)
    .reverse()
}

// Rank movement across the whole range, for risers/fallers.
export function rangeMovers(league, dates) {
  if (dates.length < 2) return []
  const first = dates[0]
  const last = dates.at(-1)
  return league.teams
    .map(team => {
      const a = valueAt(team, first, league)
      const b = valueAt(team, last, league)
      if (!a || !b) return null
      return { team, from: a.rank, to: b.rank, delta: a.rank - b.rank, scoreDelta: b.score - a.score }
    })
    .filter(Boolean)
}

export function rosterAt(snapshots, date) {
  if (!snapshots?.length || !date) return null
  let found = null
  for (const s of snapshots) {
    if (s.date <= date) found = s
    else break
  }
  return found
}

export const eventMagnitude = e => Math.abs(e.impact ?? 0)
